import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateClientMutation } from "@/api/clientApi";
import type { ClientRequest } from "@/api/clientApi";
import { toast } from "sonner";
import { Building2, MapPin } from "lucide-react";

interface CreateClientProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export default function CreateClient({ onSuccess, onClose }: CreateClientProps) {
  const [form, setForm] = useState<ClientRequest>({
    companyName: "",
    ein: "",
    contactEmail: "",
    contactPhone: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    pocName: "",
  });

  const [createClient, { isLoading }] = useCreateClientMutation();

  const update = (field: keyof ClientRequest, value: string) =>
    setForm({ ...form, [field]: value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Sanitize data: convert empty strings to null for optional fields to avoid unique constraint issues (like EIN)
      const sanitizedForm = Object.entries(form).reduce((acc, [key, value]) => {
        acc[key as keyof ClientRequest] = value === "" ? (null as any) : value;
        return acc;
      }, {} as ClientRequest);

      await createClient(sanitizedForm).unwrap();
      toast.success("Client registered successfully");
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error: any) {
      // Extract message from structured JSON error or fall back to raw data/defaults
      const message = error?.data?.message || (typeof error?.data === 'string' ? error.data : "Failed to create client");
      
      // Specifically handle unique constraint errors if the backend provides the raw DB error
      if (message.includes("clients_ein_key") || message.toLowerCase().includes("ein already exists")) {
        toast.error("A client with this EIN already exists.");
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Company Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-black flex items-center gap-2 text-slate-900 dark:text-slate-100 uppercase tracking-tighter">
          <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
            <Building2 className="h-4 w-4 text-emerald-600" />
          </div>
          Company Details
        </h3>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="companyName" className="text-[10px] font-black uppercase tracking-widest text-slate-400">Company Name *</Label>
            <Input
              id="companyName"
              placeholder="e.g. Medxpro Health"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.companyName}
              onChange={(e) => update("companyName", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ein" className="text-[10px] font-black uppercase tracking-widest text-slate-400">EIN (Tax ID)</Label>
            <Input
              id="ein"
              placeholder="12-3456789"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.ein}
              onChange={(e) => update("ein", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contactEmail" className="text-[10px] font-black uppercase tracking-widest text-slate-400">Contact Email *</Label>
            <Input
              id="contactEmail"
              type="email"
              placeholder="contact@company.com"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.contactEmail}
              onChange={(e) => update("contactEmail", e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contactPhone" className="text-[10px] font-black uppercase tracking-widest text-slate-400">Contact Phone</Label>
            <Input
              id="contactPhone"
              placeholder="(555) 000-0000"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.contactPhone}
              onChange={(e) => update("contactPhone", e.target.value)}
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="pocName" className="text-[10px] font-black uppercase tracking-widest text-slate-400">POC Name</Label>
            <Input
              id="pocName"
              placeholder="e.g. John Doe"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.pocName}
              onChange={(e) => update("pocName", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-black flex items-center gap-2 text-slate-900 dark:text-slate-100 uppercase tracking-tighter">
          <div className="p-1.5 bg-violet-50 dark:bg-violet-900/30 rounded-lg">
            <MapPin className="h-4 w-4 text-violet-600" />
          </div>
          Primary Location
        </h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="businessAddress" className="text-[10px] font-black uppercase tracking-widest text-slate-400">Street Address</Label>
            <Input
              id="businessAddress"
              placeholder="123 Corporate Blvd"
              className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
              value={form.businessAddress}
              onChange={(e) => update("businessAddress", e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="city" className="text-[10px] font-black uppercase tracking-widest text-slate-400">City</Label>
              <Input
                id="city"
                placeholder="New York"
                className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state" className="text-[10px] font-black uppercase tracking-widest text-slate-400">State</Label>
              <Input
                id="state"
                placeholder="NY"
                className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="zipCode" className="text-[10px] font-black uppercase tracking-widest text-slate-400">Zip Code</Label>
              <Input
                id="zipCode"
                placeholder="10001"
                className="h-11 rounded-xl border-slate-200 focus-visible:ring-emerald-500 font-medium"
                value={form.zipCode}
                onChange={(e) => update("zipCode", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800 mt-8">
        <Button
          type="button"
          variant="ghost"
          className="h-11 rounded-xl px-6 font-bold text-slate-500 hover:bg-slate-50"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8 font-black shadow-lg shadow-emerald-100 dark:shadow-none transition-all active:scale-95"
          disabled={isLoading}
        >
          {isLoading ? "Registering..." : "Register Client"}
        </Button>
      </div>
    </form>
  );
}
